// React adapter over `attachWidgetBridge`. Mounts the bridge for the
// lifetime of the consumer component; the cleanup tears down the
// listener so a stale iframe can't continue requesting signatures.
//
// Mount this only while a widget modal is open. Widget bundles are
// publisher-controlled, so a globally-mounted bridge would let any
// frame on the page request signatures from the user's wallet.

import { useEffect, useRef, type RefObject } from 'react';
import type { Signer } from '@cef-ai/signer';
import { attachWidgetBridge } from './attach';
import { buildEnsureSignConsent } from './ensure-consent';
import { grantWidgetSign, isWidgetSignGranted } from './consent-store';

export function useWidgetSignBridge(
  iframeRef: RefObject<HTMLIFrameElement | null>,
  signer: Signer | null,
  /**
   * Opens the consent dialog and resolves the user's choice. Only called
   * on the first sign request without a remembered grant. Held in a ref so
   * a new function identity each render does not re-attach the listener.
   */
  requestConsent: () => Promise<boolean>,
): void {
  const requestConsentRef = useRef(requestConsent);
  requestConsentRef.current = requestConsent;

  useEffect(() => {
    if (!signer) return;
    const ensureSignConsent = buildEnsureSignConsent({
      isGranted: isWidgetSignGranted,
      grant: grantWidgetSign,
      requestConsent: () => requestConsentRef.current(),
    });
    return attachWidgetBridge(window, {
      signer,
      getAllowedSource: () => iframeRef.current?.contentWindow ?? null,
      ensureSignConsent,
    });
  }, [iframeRef, signer]);
}
